<template>
  <div
    :class="[
      `design-system-input flex w-full items-center rounded-lg border transition`,
      `outline-2 -outline-offset-1 focus-within:outline`,
      isDisabled && `opacity-50`,
      fieldContext?.validationStatus.value === 'error'
        ? `border-red-300 outline-red-500 dark:border-red-800`
        : `outline-blue-500`,
    ]"
    @focusin="onFocusIn"
    @focusout="onFocusOut"
  >
    <input
      :id="fieldContext?.inputId"
      ref="input"
      v-model="modelValue"
      type="url"
      :class="[
        `block w-full border-none bg-transparent px-2.5 py-1.5 text-sm text-current placeholder:text-muted-foreground focus:outline-none focus:ring-0 disabled:bg-transparent disabled:text-current`,
      ]"
      :pattern="pattern"
      :placeholder="placeholder"
      :required="fieldContext?.required.value"
      :disabled="isDisabled"
      :aria-invalid="fieldContext?.validationStatus.value === 'error'"
      :aria-describedby="ariaDescribedBy"
      v-bind="$attrs"
      @keydown="onKeydown"
    />

    <div
      class="flex shrink-0 items-center gap-3 px-2.5 py-1.5 pl-0 empty:hidden"
    >
      <ArrowPathIcon
        v-if="linkStatus === 'pending'"
        class="inline-block size-5 animate-spin text-muted-foreground"
      />
      <CheckCircleIcon
        v-else-if="linkStatus === 'ok'"
        class="size-5 text-green-500"
      />
      <Tooltip.Root v-else-if="linkStatus === 'not_ok'">
        <Tooltip.Trigger>
          <ExclamationCircleIcon class="size-5 text-red-500" />
        </Tooltip.Trigger>

        <Tooltip.Content>
          We can't seem to reach this URL, please check if they are correct.
        </Tooltip.Content>
      </Tooltip.Root>
      <Tooltip.Root v-else-if="linkStatus === 'errored'">
        <Tooltip.Trigger>
          <ExclamationCircleIcon class="size-5 text-red-500" />
        </Tooltip.Trigger>

        <Tooltip.Content>
          Something went wrong on our side and we weren't able to verify this
          URL, please check if they are correct.
        </Tooltip.Content>
      </Tooltip.Root>

      <slot name="after" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, toRef, useTemplateRef } from "vue";

import Tooltip from "@/design_system/Tooltip";
import ArrowPathIcon from "@/icons/heroicons/arrow-path-micro.svg";
import CheckCircleIcon from "@/icons/heroicons/check-circle-mini.svg";
import ExclamationCircleIcon from "@/icons/heroicons/exclamation-circle-mini.svg";

import { injectFieldContext } from "./context";
import { useLinkValidation } from "./internal/useLinkValidation";

defineOptions({
  inheritAttrs: false,
});

const modelValue = defineModel<string>({ default: "" });

const props = defineProps<{
  pattern?: string;
  placeholder?: string;
  disabled?: boolean;
  autofocus?: boolean;
}>();

const input = useTemplateRef("input");

const fieldContext = injectFieldContext(null);

const isDisabled = computed(() => {
  return fieldContext?.disabled.value || props.disabled;
});

const ariaDescribedBy = computed(() => {
  if (!fieldContext) {
    return undefined;
  }

  const ids: string[] = [
    fieldContext.descriptionId,
    fieldContext.hintId,
    fieldContext.validationMessageId,
  ];

  return ids.join(" ");
});

const {
  status: linkStatus,
  onFocusIn,
  onFocusOut,
} = useLinkValidation(toRef(modelValue));

const onKeydown = (ev: KeyboardEvent) => {
  if (
    ev.key === "Enter" &&
    !(ev.ctrlKey || ev.metaKey || ev.shiftKey || ev.altKey)
  ) {
    ev.preventDefault();
  }
};

onMounted(() => {
  if (props.autofocus) {
    input.value?.focus({ preventScroll: true });
  }
});

defineExpose({ input });
</script>
